/**
 * HR Engine - Formulaire d'Évaluation Manager (Nouveau flux Campagnes)
 * Permet au manager d'évaluer un employé dans le cadre d'une campagne
 */

import { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Slider } from '../components/ui/slider';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import {
  Target,
  ArrowLeft,
  Loader2,
  CheckCircle2,
  Star,
  Send,
  User,
  UserCheck,
  Building2,
  Calendar,
  AlertTriangle,
  TrendingUp,
  Award,
  MessageSquare,
  Plus,
  Minus,
  Eye,
  Lock,
  Download
} from 'lucide-react';

// Niveaux de performance
const PERFORMANCE_LEVELS = {
  high_performer: { label: 'High Performer', color: 'bg-emerald-100 text-emerald-700', min: 85 },
  performer: { label: 'Performer', color: 'bg-blue-100 text-blue-700', min: 60 },
  non_performer: { label: 'Non Performer', color: 'bg-red-100 text-red-700', min: 0 }
};

const getPerformanceLevel = (score) => {
  if (score >= 85) return 'high_performer';
  if (score >= 60) return 'performer';
  return 'non_performer';
};

export default function ManagerEvaluationCampaignFormPage() {
  const params = useParams();
  const employeeId = params.employeeId;
  const evaluationIdFromRoute = params.evaluationId;
  const [searchParams] = useSearchParams();
  const campaignId = searchParams.get('campaign');
  const evaluationIdFromQuery = searchParams.get('evaluation');
  const evaluationId = evaluationIdFromRoute || evaluationIdFromQuery;
  const navigate = useNavigate();
  const { user } = useAuth();

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [evaluation, setEvaluation] = useState(null);
  const [confirmSubmit, setConfirmSubmit] = useState(false);
  const [showReturnDialog, setShowReturnDialog] = useState(false);
  const [returnReason, setReturnReason] = useState('');
  const [returning, setReturning] = useState(false);

  // Form state
  const [objectiveScores, setObjectiveScores] = useState([]);
  const [managerForm, setManagerForm] = useState({
    strengths: ['', '', ''],
    areasOfImprovement: ['', '', ''],
    globalComment: '',
    engagementLevel: 3,
    satisfactionLevel: 3,
    recommendedActions: ''
  });

  useEffect(() => {
    fetchEvaluation();
  }, [employeeId, evaluationId]);

  const fetchEvaluation = async () => {
    try {
      setLoading(true);
      let evalData = null;

      if (evaluationId) {
        // Direct evaluation ID
        const response = await api.get(`/performance-eval/evaluations/${evaluationId}`);
        evalData = response.data;
      } else if (employeeId) {
        // Find evaluation by employee and campaign
        const response = await api.get('/performance-eval/to-evaluate');
        const evals = response.data.evaluations || [];
        evalData = evals.find(e => 
          e.employeeId === employeeId && 
          (!campaignId || e.campaignId === campaignId)
        );

        if (!evalData && evals.length > 0) {
          // Take the first one for this employee
          evalData = evals.find(e => e.employeeId === employeeId);
        }

        if (evalData) {
          // Fetch full details
          const detailRes = await api.get(`/performance-eval/evaluations/${evalData.id}`);
          evalData = detailRes.data;
        }
      }

      if (!evalData) {
        toast.error('Évaluation non trouvée');
        navigate(campaignId ? `/performance/campaign/${campaignId}` : '/manager/evaluations');
        return;
      }

      setEvaluation(evalData);

      // Initialize scores from objectives
      const objectives = evalData.objectives || [];
      const scores = objectives.map(obj => ({
        objectiveId: obj.id,
        score: obj.managerScore !== null ? obj.managerScore : 50,
        comment: obj.managerComment || ''
      }));
      setObjectiveScores(scores);

      // Initialize manager form from existing evaluation
      if (evalData.managerEvaluation) {
        const me = evalData.managerEvaluation;
        setManagerForm({
          strengths: me.strengths?.length > 0 ? [...me.strengths, '', ''].slice(0, 3) : ['', '', ''],
          areasOfImprovement: me.areasOfImprovement?.length > 0 ? [...me.areasOfImprovement, '', ''].slice(0, 3) : ['', '', ''],
          globalComment: me.globalComment || '',
          engagementLevel: me.engagementLevel || 3,
          satisfactionLevel: me.satisfactionLevel || 3,
          recommendedActions: me.recommendedActions || ''
        });
      }

    } catch (error) {
      console.error('Error fetching evaluation:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const updateObjectiveScore = (objectiveId, field, value) => {
    setObjectiveScores(prev => prev.map(os =>
      os.objectiveId === objectiveId ? { ...os, [field]: value } : os
    ));
  };

  const updateStrength = (index, value) => {
    setManagerForm(prev => {
      const newStrengths = [...prev.strengths];
      newStrengths[index] = value;
      return { ...prev, strengths: newStrengths };
    });
  };

  const updateImprovement = (index, value) => {
    setManagerForm(prev => {
      const newAreas = [...prev.areasOfImprovement];
      newAreas[index] = value;
      return { ...prev, areasOfImprovement: newAreas };
    });
  };

  const calculateWeightedScore = () => {
    if (!evaluation?.objectives?.length) return 0;
    const objectives = evaluation.objectives;
    const totalWeight = objectives.reduce((sum, obj) => sum + (obj.weight || 20), 0);
    const weightedScore = objectiveScores.reduce((sum, os) => {
      const obj = objectives.find(o => o.id === os.objectiveId);
      return sum + (os.score * ((obj?.weight || 20) / totalWeight));
    }, 0);
    return Math.round(weightedScore);
  };

  const handleSubmit = async () => {
    try {
      setSubmitting(true);

      // Filter out empty strings from arrays
      const strengths = managerForm.strengths.filter(s => s.trim());
      const areasOfImprovement = managerForm.areasOfImprovement.filter(a => a.trim());

      await api.post(`/performance-eval/evaluations/${evaluation.id}/manager-eval`, {
        objectiveScores,
        strengths,
        areasOfImprovement,
        globalComment: managerForm.globalComment,
        engagementLevel: managerForm.engagementLevel,
        satisfactionLevel: managerForm.satisfactionLevel,
        recommendedActions: managerForm.recommendedActions
      });

      toast.success('Évaluation soumise avec succès !');
      setConfirmSubmit(false);
      // Retourner à la page de la campagne après soumission
      const returnUrl = evaluation?.campaignId 
        ? `/performance/campaign/${evaluation.campaignId}` 
        : (campaignId ? `/performance/campaign/${campaignId}` : '/manager/evaluations');
      navigate(returnUrl);

    } catch (error) {
      console.error('Error submitting:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la soumission');
    } finally {
      setSubmitting(false);
    }
  };

  const handleReturnToSelf = async () => {
    const reason = returnReason.trim();
    if (reason.length < 3) {
      toast.error('Merci de préciser un motif (min 3 caractères).');
      return;
    }
    try {
      setReturning(true);
      await api.post(`/performance-eval/evaluations/${evaluation.id}/return-to-self`, { reason });
      toast.success("Auto-évaluation renvoyée à l'employé. Il a été notifié par email.");
      setShowReturnDialog(false);
      setReturnReason('');
      const returnUrl = evaluation?.campaignId
        ? `/performance/campaign/${evaluation.campaignId}`
        : (campaignId ? `/performance/campaign/${campaignId}` : '/manager/evaluations');
      navigate(returnUrl);
    } catch (error) {
      console.error('Error returning to self:', error);
      toast.error(error.response?.data?.detail || "Erreur lors du renvoi à l'employé.");
    } finally {
      setReturning(false);
    }
  };

  // Politique mai 2026 (option A) : le manager ne peut éditer qu'APRÈS l'auto-évaluation.
  // Le mode "proxy" (manager remplit pour l'employé) est désactivé.
  const isProxyEvaluation = evaluation?.isProxyEvaluation === true;
  const canEdit = evaluation?.status === 'self_eval_done' || 
                  evaluation?.status === 'manager_eval_pending';
  const isCompleted = ['manager_eval_done', 'n2_validation_pending', 'validated', 'completed'].includes(evaluation?.status);
  const needsRHValidation = ['n2_validation_pending', 'manager_eval_done'].includes(evaluation?.status);
  const isAdmin = user?.role === 'Admin' || user?.role === 'SuperAdmin';
  const avgScore = calculateWeightedScore();
  const performanceLevel = getPerformanceLevel(avgScore);
  const perfConfig = PERFORMANCE_LEVELS[performanceLevel];

  if (loading) {
    return (
      <DashboardLayout title="Évaluation">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-webtech-blue" />
        </div>
      </DashboardLayout>
    );
  }

  if (!evaluation) {
    return (
      <DashboardLayout title="Évaluation">
        <Card className="py-12">
          <CardContent className="text-center">
            <AlertTriangle className="w-12 h-12 mx-auto text-amber-500 mb-4" />
            <h3 className="text-lg font-semibold mb-2">Évaluation non trouvée</h3>
            <Button onClick={() => navigate(campaignId ? `/performance/campaign/${campaignId}` : '/manager/evaluations')}>
              Retour aux évaluations
            </Button>
          </CardContent>
        </Card>
      </DashboardLayout>
    );
  }

  const emp = evaluation.employeeSnapshot || evaluation.employee || {};
  
  // Déterminer l'URL de retour (campagne si disponible, sinon liste générale)
  const backUrl = evaluation.campaignId 
    ? `/performance/campaign/${evaluation.campaignId}` 
    : (campaignId ? `/performance/campaign/${campaignId}` : '/manager/evaluations');

  return (
    <DashboardLayout title="Évaluation Manager">
      <div className="space-y-6 max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div>
            <Button
              variant="ghost"
              className="mb-2 -ml-2"
              onClick={() => navigate(backUrl)}
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Retour à la campagne
            </Button>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <Star className="h-7 w-7 text-amber-500" />
              Évaluation de {emp.firstName} {emp.lastName}
            </h1>
            <p className="text-slate-500 mt-1">
              Campagne: {evaluation.campaign?.name || 'N/A'}
            </p>
          </div>
          <div className="flex items-center gap-3">
            {/* Bouton Valider RH - visible pour Admin quand en attente de validation */}
            {needsRHValidation && isAdmin && (
              <Button
                className="bg-purple-600 hover:bg-purple-700"
                onClick={() => navigate(`/rh-validation/${evaluation.id}`)}
              >
                <CheckCircle2 className="w-4 h-4 mr-2" />
                Valider (RH)
              </Button>
            )}
            {isCompleted && (
              <Badge className={`${perfConfig?.color} text-sm`}>
                <Lock className="w-3 h-3 mr-1" />
                {perfConfig?.label} ({evaluation.finalScore}/100)
              </Badge>
            )}
          </div>
        </div>

        {/* Employee Info Card */}
        <Card className="bg-gradient-to-r from-indigo-50 to-blue-50 border-indigo-200">
          <CardContent className="py-4">
            <div className="flex items-center gap-4">
              <Avatar className="h-16 w-16 border-2 border-white shadow">
                <AvatarImage src={emp.photoUrl} />
                <AvatarFallback className="bg-gradient-to-br from-indigo-500 to-indigo-700 text-white text-lg">
                  {emp.firstName?.[0]}{emp.lastName?.[0]}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1">
                <h2 className="font-semibold text-lg text-slate-900">
                  {emp.firstName} {emp.lastName}
                </h2>
                <p className="text-slate-600">{emp.jobTitle || 'Poste non défini'}</p>
                <div className="flex items-center gap-3 mt-1">
                  <Badge variant="outline" className="text-xs">
                    <Building2 className="w-3 h-3 mr-1" />
                    {evaluation.departmentName || 'Département'}
                  </Badge>
                  {emp.matricule && (
                    <Badge variant="outline" className="text-xs">
                      #{emp.matricule}
                    </Badge>
                  )}
                </div>
              </div>
              <div className="text-center">
                <div className={`w-20 h-20 rounded-full flex items-center justify-center border-4 ${
                  avgScore >= 85 ? 'border-emerald-400 bg-emerald-50' :
                  avgScore >= 60 ? 'border-blue-400 bg-blue-50' :
                  'border-red-400 bg-red-50'
                }`}>
                  <span className={`text-2xl font-bold ${
                    avgScore >= 85 ? 'text-emerald-600' :
                    avgScore >= 60 ? 'text-blue-600' :
                    'text-red-600'
                  }`}>{avgScore}</span>
                </div>
                <p className="text-xs text-slate-500 mt-1">Score pondéré</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Historique de renvoi à l'auto-évaluation */}
        {Array.isArray(evaluation.selfEvalReturnHistory) && evaluation.selfEvalReturnHistory.length > 0 && (
          <Card className="bg-orange-50 border-orange-200" data-testid="return-history-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2 text-orange-900">
                <AlertTriangle className="w-5 h-5" />
                Renvois à l'auto-évaluation
                <Badge className="bg-orange-100 text-orange-700 ml-2">
                  {evaluation.selfEvalReturnHistory.length}
                </Badge>
              </CardTitle>
              <CardDescription className="text-orange-800">
                {evaluation.returnedToSelf
                  ? "En attente — l'employé doit reprendre son auto-évaluation."
                  : "Historique des renvois effectués pendant cette évaluation."}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-2">
              {evaluation.selfEvalReturnHistory.map((entry, idx) => (
                <div key={entry.returnedAt || idx} className="bg-white rounded-md border border-orange-200 px-3 py-2">
                  <p className="text-xs text-orange-600">
                    {entry.returnedAt ? new Date(entry.returnedAt).toLocaleString('fr-FR') : ''}
                  </p>
                  <p className="text-sm text-slate-800 mt-0.5 whitespace-pre-wrap break-words">
                    {entry.reason}
                  </p>
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Self-evaluation summary */}
        {evaluation.selfEvaluation && (
          <Card className="bg-blue-50 border-blue-200">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2 text-blue-800">
                <User className="w-5 h-5" />
                Auto-évaluation de l'employé
                {isProxyEvaluation && (
                  <Badge className="bg-purple-100 text-purple-700 ml-2">
                    Remplie par le manager
                  </Badge>
                )}
                <Badge className="bg-blue-100 text-blue-700 ml-2">
                  {evaluation.selfScore}/100
                </Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className="text-sm text-blue-700 space-y-2">
              {evaluation.selfEvaluation.achievements && (
                <div>
                  <span className="font-medium">Réalisations :</span> {evaluation.selfEvaluation.achievements}
                </div>
              )}
              {evaluation.selfEvaluation.challenges && (
                <div>
                  <span className="font-medium">Défis :</span> {evaluation.selfEvaluation.challenges}
                </div>
              )}
              {evaluation.selfEvaluation.globalComment && (
                <div>
                  <span className="font-medium">Commentaire :</span> {evaluation.selfEvaluation.globalComment}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Proxy evaluation legacy notice — bloqué depuis mai 2026 (option A) */}
        {isProxyEvaluation && evaluation.status === 'self_eval_pending' && (
          <Card className="bg-red-50 border-red-200">
            <CardContent className="py-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-red-900">Évaluation bloquée — l'employé n'a pas de compte</p>
                  <p className="text-sm text-red-700 mt-1">
                    Depuis mai 2026, l'auto-évaluation est réservée à l'employé lui-même.
                    Cet employé ne dispose pas d'un identifiant de connexion (email, téléphone ou matricule).
                  </p>
                  <p className="text-sm text-red-700 mt-2 font-medium">
                    → Demandez à votre DRH d'ajouter un identifiant sur la fiche employé, puis l'employé pourra se connecter et réaliser son auto-évaluation.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Status warning */}
        {!canEdit && !isCompleted && (
          <Card className="bg-amber-50 border-amber-200">
            <CardContent className="py-4">
              <div className="flex items-center gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-600" />
                <div>
                  <p className="font-medium text-amber-900">En attente de l'auto-évaluation</p>
                  <p className="text-sm text-amber-700">
                    L'employé doit d'abord soumettre son auto-évaluation avant que vous puissiez l'évaluer.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Objectives */}
        <div className="space-y-4">
          <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
            <Target className="w-5 h-5 text-indigo-500" />
            Objectifs à évaluer ({evaluation.objectives?.length || 0})
          </h2>

          {evaluation.objectives?.length === 0 ? (
            <Card className="py-8 text-center">
              <CardContent>
                <Target className="w-12 h-12 mx-auto text-slate-300 mb-2" />
                <p className="text-slate-500">Aucun objectif défini</p>
              </CardContent>
            </Card>
          ) : (
            evaluation.objectives?.map((objective, index) => {
              const scoreData = objectiveScores.find(os => os.objectiveId === objective.id) || {};
              const selfScore = objective.selfScore;
              const managerScore = scoreData.score || 50;
              const gap = selfScore !== null ? Math.abs(selfScore - managerScore) : 0;

              return (
                <Card key={objective.id} className="border-l-4 border-l-amber-500">
                  <CardContent className="pt-4">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <Badge variant="outline" className="text-amber-600">
                            Objectif {index + 1}
                          </Badge>
                          <Badge className="bg-slate-100 text-slate-700">
                            {objective.weight || 20}%
                          </Badge>
                        </div>
                        <h3 className="font-semibold text-slate-900">{objective.title}</h3>
                      </div>
                      <div className="text-right">
                        <p className="text-2xl font-bold text-amber-600">{managerScore}</p>
                        <p className="text-xs text-slate-500">/100</p>
                      </div>
                    </div>

                    {objective.description && (
                      <p className="text-sm text-slate-600 mb-3">{objective.description}</p>
                    )}

                    {objective.indicators && (
                      <div className="text-sm bg-slate-50 p-2 rounded mb-3">
                        <span className="font-medium">Indicateurs: </span>
                        {objective.indicators}
                      </div>
                    )}

                    {/* Self-evaluation display — 3 champs distincts */}
                    {selfScore !== null && (
                      <div className="bg-blue-50 p-3 rounded-lg mb-3 border border-blue-200 space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-blue-800 flex items-center gap-1">
                            <User className="w-4 h-4" />
                            Auto-évaluation
                          </span>
                          <Badge className="bg-blue-100 text-blue-700">{selfScore}/100</Badge>
                        </div>
                        {objective.selfAchievements && (
                          <div className="text-sm text-blue-700">
                            <span className="font-medium">Principales réalisations :</span>{' '}
                            <span className="italic">{objective.selfAchievements}</span>
                          </div>
                        )}
                        {objective.selfChallenges && (
                          <div className="text-sm text-blue-700">
                            <span className="font-medium">Défis rencontrés :</span>{' '}
                            <span className="italic">{objective.selfChallenges}</span>
                          </div>
                        )}
                        {objective.selfComment && (
                          <div className="text-sm text-blue-700">
                            <span className="font-medium">Commentaire :</span>{' '}
                            <span className="italic">"{objective.selfComment}"</span>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Manager evaluation */}
                    <div className="space-y-3 pt-3 border-t">
                      <div>
                        <Label className="text-sm font-medium flex items-center justify-between">
                          <span className="flex items-center gap-1">
                            <Star className="w-4 h-4 text-amber-500" />
                            Votre évaluation
                          </span>
                          <span className={`text-lg font-bold ${
                            managerScore >= 85 ? 'text-emerald-600' :
                            managerScore >= 60 ? 'text-blue-600' :
                            'text-red-600'
                          }`}>
                            {managerScore}/100
                          </span>
                        </Label>
                        <Slider
                          value={[managerScore]}
                          onValueChange={([v]) => updateObjectiveScore(objective.id, 'score', v)}
                          min={0}
                          max={100}
                          step={1}
                          disabled={!canEdit}
                          className="my-2"
                        />
                        <div className="flex justify-between text-xs text-slate-400">
                          <span>0 - Non atteint</span>
                          <span>50 - Partiellement</span>
                          <span>100 - Dépassé</span>
                        </div>
                      </div>

                      {/* Gap warning */}
                      {gap >= 20 && selfScore !== null && (
                        <div className="flex items-center gap-2 text-amber-600 text-sm bg-amber-50 p-2 rounded-lg">
                          <AlertTriangle className="w-4 h-4" />
                          Écart de {gap} points avec l'auto-évaluation
                        </div>
                      )}

                      <div>
                        <Label className="text-sm">Votre commentaire</Label>
                        <Textarea
                          value={scoreData.comment || ''}
                          onChange={(e) => updateObjectiveScore(objective.id, 'comment', e.target.value)}
                          placeholder="Commentaire sur la performance de cet objectif..."
                          rows={2}
                          disabled={!canEdit}
                        />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })
          )}
        </div>

        {/* Feedback global */}
        {(canEdit || isCompleted) && (
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <MessageSquare className="w-5 h-5 text-slate-500" />
                Feedback global
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Points forts */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2 text-green-700">
                  <TrendingUp className="w-4 h-4" />
                  Points forts (3 max)
                </Label>
                {managerForm.strengths.map((strength, idx) => (
                  <Input
                    key={idx}
                    value={strength}
                    onChange={(e) => updateStrength(idx, e.target.value)}
                    placeholder={`Point fort ${idx + 1}`}
                    disabled={!canEdit}
                  />
                ))}
              </div>

              {/* Axes d'amélioration */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2 text-amber-700">
                  <AlertTriangle className="w-4 h-4" />
                  Axes d'amélioration (3 max)
                </Label>
                {managerForm.areasOfImprovement.map((area, idx) => (
                  <Input
                    key={idx}
                    value={area}
                    onChange={(e) => updateImprovement(idx, e.target.value)}
                    placeholder={`Axe d'amélioration ${idx + 1}`}
                    disabled={!canEdit}
                  />
                ))}
              </div>

              {/* Niveaux */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Niveau d'engagement</Label>
                  <Select
                    value={String(managerForm.engagementLevel)}
                    onValueChange={(v) => setManagerForm({...managerForm, engagementLevel: parseInt(v)})}
                    disabled={!canEdit}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">1 - Très faible</SelectItem>
                      <SelectItem value="2">2 - Faible</SelectItem>
                      <SelectItem value="3">3 - Moyen</SelectItem>
                      <SelectItem value="4">4 - Bon</SelectItem>
                      <SelectItem value="5">5 - Excellent</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Niveau de satisfaction</Label>
                  <Select
                    value={String(managerForm.satisfactionLevel)}
                    onValueChange={(v) => setManagerForm({...managerForm, satisfactionLevel: parseInt(v)})}
                    disabled={!canEdit}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">1 - Très insatisfait</SelectItem>
                      <SelectItem value="2">2 - Insatisfait</SelectItem>
                      <SelectItem value="3">3 - Neutre</SelectItem>
                      <SelectItem value="4">4 - Satisfait</SelectItem>
                      <SelectItem value="5">5 - Très satisfait</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Commentaire global */}
              <div className="space-y-2">
                <Label>Commentaire global</Label>
                <Textarea
                  value={managerForm.globalComment}
                  onChange={(e) => setManagerForm({...managerForm, globalComment: e.target.value})}
                  placeholder="Commentaire général sur la performance de cet employé..."
                  rows={3}
                  disabled={!canEdit}
                />
              </div>

              {/* Actions recommandées */}
              <div className="space-y-2">
                <Label>Actions recommandées</Label>
                <Textarea
                  value={managerForm.recommendedActions}
                  onChange={(e) => setManagerForm({...managerForm, recommendedActions: e.target.value})}
                  placeholder="Formation, coaching, projet spécifique..."
                  rows={2}
                  disabled={!canEdit}
                />
              </div>
            </CardContent>
          </Card>
        )}

        {/* Performance summary */}
        <Card className={`${perfConfig?.color.replace('text-', 'border-').split(' ')[0]} border-2`}>
          <CardContent className="py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Award className={`w-8 h-8 ${
                  avgScore >= 85 ? 'text-emerald-600' :
                  avgScore >= 60 ? 'text-blue-600' :
                  'text-red-600'
                }`} />
                <div>
                  <p className="font-semibold text-slate-900">Niveau de performance</p>
                  <Badge className={perfConfig?.color}>{perfConfig?.label}</Badge>
                </div>
              </div>
              <div className="text-right">
                <p className="text-3xl font-bold text-slate-900">{avgScore}/100</p>
                <p className="text-sm text-slate-500">Score final pondéré</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Submit button */}
        {canEdit && (
          <div className="flex flex-col sm:flex-row justify-end gap-3 pt-4 border-t">
            <Button variant="outline" onClick={() => navigate(backUrl)} data-testid="manager-eval-cancel-btn">
              Annuler
            </Button>
            {/* Renvoyer à l'auto-évaluation : le bouton s'affiche toujours en mode édition.
                Le backend refusera proprement si l'employé n'a pas de compte (vrai proxy). */}
            <Button
              variant="outline"
              className="border-orange-300 text-orange-700 hover:bg-orange-50 hover:text-orange-800"
              onClick={() => setShowReturnDialog(true)}
              data-testid="return-to-self-btn"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Renvoyer à l'employé
            </Button>
            <Button
              className="bg-green-600 hover:bg-green-700"
              onClick={() => setConfirmSubmit(true)}
              data-testid="manager-eval-submit-btn"
            >
              <Send className="w-4 h-4 mr-2" />
              Soumettre mon évaluation
            </Button>
          </div>
        )}

        {/* Completed actions */}
        {isCompleted && (
          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button variant="outline" onClick={() => navigate(backUrl)}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Retour
            </Button>
            <Button 
              variant="outline"
              onClick={async () => {
                try {
                  toast.info('Génération du PDF en cours...');
                  const response = await api.get(`/performance-eval/evaluations/${evaluation.id}/pdf`, {
                    responseType: 'blob'
                  });
                  
                  // Créer un lien de téléchargement
                  const blob = new Blob([response.data], { type: 'application/pdf' });
                  const url = window.URL.createObjectURL(blob);
                  const link = document.createElement('a');
                  link.href = url;
                  link.download = `Evaluation_${emp.firstName}_${emp.lastName}_${new Date().toISOString().split('T')[0]}.pdf`;
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                  window.URL.revokeObjectURL(url);
                  
                  toast.success('PDF téléchargé avec succès !');
                } catch (error) {
                  console.error('Erreur téléchargement PDF:', error);
                  toast.error('Erreur lors du téléchargement du PDF');
                }
              }}
            >
              <Download className="w-4 h-4 mr-2" />
              Télécharger PDF
            </Button>
          </div>
        )}

        {/* Confirm dialog */}
        <AlertDialog open={confirmSubmit} onOpenChange={setConfirmSubmit}>
          <AlertDialogContent data-testid="manager-confirm-submit-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-amber-500" />
                Confirmer la soumission de l'évaluation
              </AlertDialogTitle>
              <AlertDialogDescription asChild>
                <div className="space-y-3 text-sm text-slate-600">
                  <p>
                    Vous vous apprêtez à soumettre votre évaluation pour{' '}
                    <span className="font-semibold text-slate-900">
                      {emp.firstName} {emp.lastName}
                    </span>.
                  </p>

                  {/* Score récap */}
                  <div className="rounded-md border border-slate-200 bg-slate-50 p-3 flex items-center justify-between">
                    <span className="text-slate-500">Score attribué</span>
                    <span className="flex items-center gap-2">
                      <span className={
                        `font-bold text-lg ${avgScore >= 85 ? 'text-emerald-600' :
                        avgScore >= 60 ? 'text-blue-600' :
                        'text-red-600'}`
                      }>{avgScore}/100</span>
                      {perfConfig?.label && (
                        <Badge className={perfConfig.color}>{perfConfig.label}</Badge>
                      )}
                    </span>
                  </div>

                  {/* Avertissement fort */}
                  <div className="rounded-md bg-amber-50 border border-amber-300 p-3 space-y-2">
                    <p className="font-semibold text-amber-900">
                      ⚠ Vérifiez bien avant de soumettre
                    </p>
                    <p className="text-amber-800 text-xs leading-relaxed">
                      Une fois soumise, cette évaluation sera <strong>verrouillée</strong> et
                      transmise au processus de validation RH. Vous ne pourrez plus la modifier.
                    </p>
                    <div className="pt-1 space-y-1 text-xs text-amber-900">
                      <p className="font-medium">Avant de confirmer, assurez-vous d'avoir :</p>
                      <ul className="list-disc ml-5 space-y-0.5 text-amber-800">
                        <li>Vérifié chaque note attribuée par objectif</li>
                        <li>Relu vos commentaires et justifications</li>
                        <li>Comparé avec l'auto-évaluation de {emp.firstName}</li>
                        <li>Pris en compte les éléments contextuels de l'année</li>
                      </ul>
                    </div>
                    <p className="text-xs text-amber-800 pt-1 border-t border-amber-200">
                      💡 Si l'auto-évaluation vous semble incomplète, utilisez plutôt le bouton
                      <strong> « Renvoyer à l'employé »</strong>.
                    </p>
                  </div>
                </div>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="manager-confirm-submit-cancel">
                Annuler, je veux relire
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={handleSubmit}
                disabled={submitting}
                className="bg-green-600 hover:bg-green-700"
                data-testid="manager-confirm-submit-confirm"
              >
                {submitting ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Send className="w-4 h-4 mr-2" />
                )}
                Je confirme, soumettre l'évaluation
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Return-to-self dialog */}
        <AlertDialog open={showReturnDialog} onOpenChange={(open) => { setShowReturnDialog(open); if (!open) setReturnReason(''); }}>
          <AlertDialogContent data-testid="return-to-self-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle>Renvoyer l'auto-évaluation à {emp?.firstName} {emp?.lastName}</AlertDialogTitle>
              <AlertDialogDescription asChild>
                <div className="space-y-3 text-sm text-slate-600">
                  <p>
                    L'employé sera notifié par email et pourra reprendre son auto-évaluation.
                    Ses réponses actuelles sont conservées — il pourra simplement les modifier.
                  </p>
                  <p className="text-slate-900 font-medium">
                    Merci d'indiquer un motif :
                  </p>
                </div>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <Textarea
              value={returnReason}
              onChange={(e) => setReturnReason(e.target.value)}
              placeholder="Ex : Les éléments sur l'objectif 2 méritent plus de détails chiffrés, merci de préciser les réalisations concrètes."
              rows={5}
              className="resize-none"
              data-testid="return-to-self-reason"
            />
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="return-to-self-cancel">Annuler</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleReturnToSelf}
                disabled={returning || returnReason.trim().length < 3}
                className="bg-orange-600 hover:bg-orange-700"
                data-testid="return-to-self-confirm"
              >
                {returning ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <ArrowLeft className="w-4 h-4 mr-2" />
                )}
                Renvoyer à l'employé
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </DashboardLayout>
  );
}
